<template>
  <component
    :is="tag"
    class="button"
    :class="{
      [size]: true,
      [type]: true,
      [color]: true,
      'icon-left': (icon || loading) && !iconRight,
      'icon-right': (icon || loading) && iconRight,
      'with-chevron': chevron,
      'icon-only': iconOnlyDetected,
      'block-button': block,
    }"
    :disabled="disabled"
    :type="buttonType"
    v-bind="$attrs"
  >
    <SvgIcon v-if="!loading && icon && !iconRight" class="icon" :name="icon" />
    <div v-if="loading && !iconRight" class="icon loading-icon">
      <Loader class="loader" />
    </div>
    <span class="label">
      <slot v-if="!iconOnlyDetected" />
    </span>
    <div v-if="loading && iconRight" class="icon loading-icon">
      <Loader class="loader" />
    </div>
    <SvgIcon v-if="!loading && icon && iconRight" class="icon" :name="icon" />
    <SvgIcon v-if="chevron" class="chevron" :name="chevron" />
  </component>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import { computed, useSlots } from 'vue'

import Loader from '@/components/common/loader/Loader.vue'
import SvgIcon from '@/components/common/SvgIcon.vue'

const slots = useSlots()

const props = defineProps({
  size: { type: String as PropType<'small' | 'medium' | 'large'>, default: 'medium' },
  type: { type: String as PropType<'primary' | 'secondary' | 'text'>, default: 'primary' },
  color: {
    type: String as PropType<'key' | 'gray' | 'danger' | 'warning' | 'accent'>,
    default: 'key',
  },
  tag: { type: String, default: 'button' },
  icon: { type: String },
  chevron: { type: String },
  loading: { type: Boolean, default: false },
  iconRight: { type: Boolean, default: false },
  iconOnly: { type: Boolean, default: false },
  block: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  buttonType: { type: String as PropType<'button' | 'submit' | 'reset'>, default: 'button' },
})

const iconOnlyDetected = computed(() => props.iconOnly || !slots.default)
</script>

<style scoped lang="postcss">
.button {
  @apply inline-flex items-center justify-center align-middle rounded-full
  text-base font-bold border transition-all select-none whitespace-nowrap;

  .loader {
    @apply w-full h-full text-gray-900;
  }

  &.block-button {
    @apply flex w-full;
  }

  .icon {
    @apply flex-shrink-0;
  }

  &:not(.icon-only) {
    .label {
      @apply flex-grow;
    }
  }

  /* extra CSS selectors specificity needed to reset shadows in one place */
  &.small.primary[disabled],
  &.medium.primary[disabled],
  &.large.primary[disabled],
  &.small.secondary[disabled],
  &.medium.secondary[disabled],
  &.large.secondary[disabled] {
    @apply cursor-not-allowed shadow-none hover:shadow-none active:shadow-none;
  }

  /* Fixes the ink outside of rounded borders issue in safari, see https://stackoverflow.com/a/23735769/2848264 */
  transform: translateZ(0);

  /* optimize browser work on hover */
  transition-property: box-shadow, background-color, border-color, color, transform;

  &.small {
    @apply text-sm;

    /* to have nice buttons we need to subtract border width */
    padding: 3px 12px;

    .icon {
      @apply w-4 h-4;
    }

    .label {
      @apply text-sm overflow-ellipsis font-bold;
    }
  }

  &.medium {
    /* to have nice buttons we need to subtract border width */
    padding: 7px 20px;
    @apply italic;

    .icon {
      @apply block w-5 h-5;
    }

    .label {
      @apply text-base overflow-ellipsis font-extrabold;
    }
  }

  &.large {
    @apply text-lg italic;

    /* to have nice buttons we need to subtract border width */
    padding: 11px 32px;

    .icon {
      @apply block w-6 h-6;
    }

    .label {
      @apply text-lg-button overflow-ellipsis font-extrabold;
    }
  }

  &.primary {
    @apply text-gray-900;

    &[disabled] {
      @apply text-gray-900-60;
    }

    .loader {
      @apply text-gray-900;
    }
  }

  &.secondary {
    @apply border-white-10
    hover:bg-white-5 hover:border-white-20
    active:bg-white-10 active:border-white-20;

    &[disabled] {
      @apply bg-white-10 border-white-20 hover:bg-white-10 hover:border-white-20;
    }
  }

  &.text {
    @apply border-transparent bg-transparent shadow-none hover:shadow-none hover:border-white-20 active:border-white-40;

    &[disabled] {
      @apply border-transparent bg-transparent hover:border-transparent  hover:bg-transparent;
    }
  }

  &.key {
    &.primary {
      @apply bg-key-500 border-key-500
      hover:border-key-400 hover:bg-key-400
      active:border-key-600 active:bg-key-600;

      &[disabled] {
        @apply bg-key-600 border-key-600 hover:bg-key-600 hover:border-key-600 text-gray-900-60;
      }
    }

    &.secondary {
      @apply text-key-500 hover:text-key-500;

      &[disabled] {
        @apply text-key-500-60 hover:text-key-500-60;
      }

      .loader {
        @apply text-key-500 hover:text-key-500;
      }
    }
    &.text {
      @apply text-white-60 hover:text-white active:text-white;

      &[disabled] {
        @apply text-white-40 hover:text-white-40;
      }

      .loader {
        @apply text-white-60 hover:text-white active:text-white;
      }
    }
  }

  &.danger {
    &.primary {
      @apply bg-danger-500 border-danger-500
      hover:border-danger-400 hover:bg-danger-400
      active:border-danger-600 active:bg-danger-600;

      &[disabled] {
        @apply bg-danger-600 hover:border-danger-600 border-danger-600 hover:border-danger-600 hover:text-gray-900-60;
      }
    }

    &.secondary,
    &.text {
      @apply text-danger-500 hover:text-danger-500;

      &[disabled] {
        @apply text-danger-500-60 hover:text-danger-500-60;
      }

      .loader {
        @apply text-danger-500 hover:text-danger-500;
      }
    }
  }

  &.warning {
    &.primary {
      @apply bg-warning-400 border-warning-400
      hover:border-warning-300 hover:bg-warning-300
      active:border-warning-500 active:bg-warning-500;

      &[disabled] {
        @apply bg-warning-500 hover:border-warning-500 border-warning-500 hover:text-gray-900-60;
      }
    }

    &.secondary,
    &.text {
      @apply text-warning-400 hover:text-warning-400;

      &[disabled] {
        @apply text-warning-400 hover:text-warning-400;
      }

      .loader {
        @apply text-warning-400 hover:text-warning-400;
      }
    }
  }

  &.small.icon-left {
    @apply pl-2;

    .icon {
      @apply mr-2;
    }

    &.block-button {
      @apply px-2;

      .label {
        @apply mr-4 pr-2;
      }
    }
  }

  &.small.icon-right {
    @apply pr-2;

    .icon {
      @apply ml-2;
    }

    &.block-button {
      @apply px-2;

      .label {
        @apply ml-4 pl-2;
      }
    }
  }

  &.small.with-chevron {
    @apply pr-2;

    .chevron {
      @apply m-0 w-3 h-3 ml-2;
    }
  }

  &.medium.icon-left {
    @apply pl-4;

    .icon {
      @apply mr-3;
    }

    &.block-button {
      @apply px-4;

      .label {
        @apply mr-5 pr-4;
      }
    }
  }

  &.medium.icon-right {
    @apply pr-4;

    .icon {
      @apply ml-3;
    }

    &.block-button {
      @apply px-4;

      .label {
        @apply ml-5 pl-4;
      }
    }
  }

  &.medium.with-chevron {
    @apply pr-4;

    .chevron {
      @apply m-0 w-3 h-3 ml-3;
    }
  }

  &.large.icon-left {
    @apply pl-5;

    .icon {
      @apply mr-4;
    }

    &.block-button {
      @apply px-5;

      .label {
        @apply mr-6 pr-5;
      }
    }
  }

  &.large.icon-right {
    @apply pr-5;

    .icon {
      @apply ml-5;
    }

    &.block-button {
      @apply px-5;

      .label {
        @apply ml-6 pl-5;
      }
    }
  }

  &.large.with-chevron {
    @apply pr-5;

    .chevron {
      @apply m-0 w-4 h-4 ml-5;
    }
  }

  &.icon-only {
    @apply rounded-full;

    &.icon-left .icon,
    &.icon-right .icon {
      @apply m-0;
    }

    &.button {
      @apply w-9 h-9 p-0;

      &.small {
        @apply w-6 h-6;
      }

      &.large {
        @apply w-12 h-12;

        &.text.gray {
          @apply text-gray-600 hover:text-gray-900 hover:border hover:border-gray-200 active:border-gray-300;
        }
      }
    }
  }
}
</style>
